import {i18n} from '../utils/I18n';
import {SessionService} from './session.service';
import {Dayjs} from "dayjs";
import {formatIsoDate} from "../utils/date";

export interface TrainInformation {
	validFrom?: string;
	validTo?: string;
	seat: CategoryInformation;
	couchette: CategoryInformation;
	sleeper: CategoryInformation;
	wheelchair: CategoryInformation;
}

export interface CategoryInformation {
	highlightedProperties: string[];
	regularProperties: string[];
	reservationProperties: string[];
	images?: CompartmentImage[];
}

export interface CompartmentImage {
	image: string;
	caption?: string;
	copyright?: string;
	title?: string;
	width?: number;
	height?: number;
}

export class TrainInformationService {

	private static _instance: TrainInformationService;

	private data = new Map<string, Promise<TrainInformation[]>>();

	static get instance(): TrainInformationService {
		if (!TrainInformationService._instance) {
			TrainInformationService._instance = new TrainInformationService();
		}
		return TrainInformationService._instance;
	}

	async getTrainInformation(trainCode: string, date: Dayjs): Promise<TrainInformation> {
		if (!this.data.has(trainCode)) {
			this.data.set(trainCode, this.fetchTrainInformation(trainCode));
		}

		const trainData = await this.data.get(trainCode);
		if (!trainData?.length) {
			return null;
		}

		const dateString = formatIsoDate(date);
		return trainData.filter(item =>
			// Filter for matching date-range
			(!item.validFrom || item.validFrom.localeCompare(dateString) <= 0) && (!item.validTo || item.validTo.localeCompare(dateString) >= 0)
		).reduce((a, b) => {
			// If there are multiple matching date ranges use the smallest range
			if (a.validFrom && !b.validFrom) {
				// validFrom only set on first item: Use first item
				return a;
			} else if (b.validFrom && !a.validFrom) {
				// validFrom only set on second item: Use second item
				return b;
			} else if (a.validFrom && b.validFrom && a.validFrom !== b.validFrom) {
				// validFrom set to different values: Use later value
				return a.validFrom.localeCompare(b.validFrom) < 0 ? b : a;
			} else if (a.validTo && !b.validTo) {
				// validTo only set on first item: Use first item
				return a;
			} else if (b.validTo && !a.validTo) {
				// validTo only set on second item: Use second item
				return b;
			} else if (a.validTo && b.validTo && a.validTo !== b.validTo) {
				// validTo set to different values: Use earlier value
				return a.validTo.localeCompare(b.validTo) < 0 ? a : b;
			} else {
				// Both values identical: Just keep the existing order
				return a;
			}
		});
	}

	private async fetchTrainInformation(trainCode: string): Promise<TrainInformation[]> {
		const baseUrl = SessionService.instance.getMagnoliaBaseUrl();
		const lang = i18n.getLang();
		const response = await fetch(`${baseUrl}/.rest/nightjet-trains/${lang}/${trainCode}`);

		if (!response.ok) {
			throw new Error(`Failed to load train information for ${trainCode}: ${response.status}`);
		}

		return await response.json();
	}
}
